import type * as React from 'react';

export type PageHeaderAction = {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Fires before navigation when combined with `to`. */
  onClick?: () => void;
  to?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  disabled?: boolean;
  /**
   * Why this action is unavailable — e.g. the RBAC "admins only" reason.
   * Setting it BOTH disables the action and asks the host app to explain the
   * disabling; a bare `disabled: true` stays silent. This package renders no
   * tooltip of its own (it must not depend on one), so the host supplies a
   * `renderDisabled` hook — see PageHeaderProps.
   *
   * A reason never MOVES an action: it keeps whatever inline/⋮ slot
   * `splitPageHeaderActions` gave it. That is the whole point — it exists so a
   * permission-gated action can stay where the design put it instead of being
   * hand-rolled into `extra`.
   */
  disabledReason?: string;
  /** Always rendered inside the ⋮ menu, never inline. */
  destructive?: boolean;
  hidden?: boolean;
};

export const INLINE_ACTION_LIMIT = 2;

/**
 * The one "is this action live?" predicate. Every render path that might turn
 * an action into a live `<Link>` reads this instead of bare `action.disabled`,
 * so an action disabled only by a `disabledReason` can never slip through as
 * one. (The plain, non-link menu item — no `to`, no `disabledReason` — reads
 * `action.disabled` directly; that branch only runs when `disabledReason` is
 * already falsy, where this function reduces to the same value.)
 */
export function actionIsDisabled(action: PageHeaderAction): boolean {
  return Boolean(action.disabled) || Boolean(action.disabledReason);
}

/** Shared sizing for PageHeader action buttons (inline actions and `extra`
 * controls), so both stack/size identically on mobile vs desktop. */
export const pageHeaderActionClass = 'h-[38.4px] flex-1 sm:h-8 sm:flex-none';

export function splitPageHeaderActions(actions: PageHeaderAction[] | undefined): {
  inline: PageHeaderAction[];
  menu: PageHeaderAction[];
} {
  const visible = (actions ?? []).filter((a) => !a.hidden);
  const inline: PageHeaderAction[] = [];
  const menu: PageHeaderAction[] = [];
  for (const action of visible) {
    if (!action.destructive && inline.length < INLINE_ACTION_LIMIT) inline.push(action);
    else menu.push(action);
  }
  return { inline, menu };
}
